import { useEffect, useMemo, useState } from "react";
import { OrderList } from "primereact/orderlist";
import type { OrderListChangeEvent } from "primereact/orderlist";
import { themeClasses } from "sj-ui-toolkit/src/atlas/utils";
import type { Lesson } from "@/api/lessons";

interface LessonWithOrder extends Lesson {
  displayOrder?: number;
}

const LessonOrderTemplate = (lesson: LessonWithOrder) => {
  return (
    <div className="flex align-items-center gap-2 m-2">
      <span className="font-bold">{lesson.displayOrder}.</span>
      <div className="flex-1 flex flex-col gap-2">
        <span className="font-bold">{lesson.label ?? lesson.title ?? lesson.name}</span>
        <div className="flex align-items-center gap-2">
          <span>{lesson.description}</span>
        </div>
      </div>
    </div>
  );
};

interface LessonOrderProps {
  lessonMap: Record<string, Lesson>;
  lessonOrder: string[];
  handleChange: (keys: string[]) => void;
}

export const LessonOrder = ({
  lessonMap = {},
  lessonOrder = [],
  handleChange = () => undefined,
}: LessonOrderProps) => {
  const [selectedLessonOrder, setSelectedLessonOrder] = useState<Lesson[]>([]);

  const numberedLessons: LessonWithOrder[] = useMemo(
    () => selectedLessonOrder.map((lesson, idx) => ({ ...lesson, displayOrder: idx + 1 })),
    [selectedLessonOrder]
  );

  useEffect(() => {
    if (Object.keys(lessonMap).length === 0 || lessonOrder.length === 0) {
      setSelectedLessonOrder([]);
    } else {
      setSelectedLessonOrder(
        lessonOrder.map(key => lessonMap[key]).filter((l): l is Lesson => l !== undefined)
      );
    }
  }, [lessonMap, lessonOrder]);

  const handleListChange = (updated: Lesson[]) => {
    setSelectedLessonOrder(updated);
    handleChange(
      updated.map(lesson => lesson.lessonKey ?? lesson.lesson_key ?? "").filter(Boolean)
    );
  };

  return (
    <div className="p-4" data-testid="lesson-order-container">
      <div className="flex flex-wrap gap-4">
        <div className="flex-1 min-w-0">
          {numberedLessons.length === 0 ? (
            <p className={themeClasses.textGray500} data-testid="lesson-order-empty-message">
              No Lessons selected for this Assignment
            </p>
          ) : (
            <OrderList
              dataKey="id"
              value={numberedLessons}
              onChange={(e: OrderListChangeEvent) => handleListChange(e.value as Lesson[])}
              itemTemplate={LessonOrderTemplate}
              header="Set order of Lessons for the Assignment"
              data-testid="lesson-order-list"
            />
          )}
        </div>
      </div>
    </div>
  );
};
